/**
 * Create string representation of a location
 */
export function stringifyLocation(x) {
    return [
        x.streetAddress.line1,
        x.streetAddress.line2,
        "\n",
        x.streetAddress.city + ",",
        x.streetAddress.state,
        x.streetAddress.zip,
    ].join(" ");
}
/**
 * Calculates the haversine distance (as the crow flies) in miles between two Locations.
 * @param x First location
 * @param y Second location
 */
export function distance(x, y) {
    const toRad = (x) => (x * Math.PI) / 180;
    const fromRad = (x) => (x / Math.PI) * 180;
    const theta = toRad(x.geopoint.longitude) - toRad(y.geopoint.longitude);
    let dist = Math.sin(toRad(x.geopoint.latitude)) * Math.sin(toRad(y.geopoint.latitude)) +
        Math.cos(toRad(x.geopoint.latitude)) * Math.cos(toRad(y.geopoint.latitude)) * Math.cos(theta);
    dist = Math.acos(dist);
    return fromRad(dist) * 60 * 1.1515;
}
export const states = {
    AL: "Alabama",
    AK: "Alaska",
    // AS: "American Samoa",
    AZ: "Arizona",
    AR: "Arkansas",
    CA: "California",
    CO: "Colorado",
    CT: "Connecticut",
    DE: "Delaware",
    DC: "District Of Columbia",
    // FM: "Federated States Of Micronesia",
    FL: "Florida",
    GA: "Georgia",
    // GU: "Guam",
    HI: "Hawaii",
    ID: "Idaho",
    IL: "Illinois",
    IN: "Indiana",
    IA: "Iowa",
    KS: "Kansas",
    KY: "Kentucky",
    LA: "Louisiana",
    ME: "Maine",
    // MH: "Marshall Islands",
    MD: "Maryland",
    MA: "Massachusetts",
    MI: "Michigan",
    MN: "Minnesota",
    MS: "Mississippi",
    MO: "Missouri",
    MT: "Montana",
    NE: "Nebraska",
    NV: "Nevada",
    NH: "New Hampshire",
    NJ: "New Jersey",
    NM: "New Mexico",
    NY: "New York",
    NC: "North Carolina",
    ND: "North Dakota",
    // MP: "Northern Mariana Islands",
    OH: "Ohio",
    OK: "Oklahoma",
    OR: "Oregon",
    // PW: "Palau",
    PA: "Pennsylvania",
    // PR: "Puerto Rico",
    RI: "Rhode Island",
    SC: "South Carolina",
    SD: "South Dakota",
    TN: "Tennessee",
    TX: "Texas",
    UT: "Utah",
    VT: "Vermont",
    // VI: "Virgin Islands",
    VA: "Virginia",
    WA: "Washington",
    WV: "West Virginia",
    WI: "Wisconsin",
    WY: "Wyoming",
};
// Calculate the upper and lower boundary geohashes for
// a given latitude, longitude, and distance in miles
export const getGeohashRange = (latitude, longitude, distance // miles
) => {
    const lat = 0.0144927536231884; // degrees latitude per mile
    const lon = 0.0181818181818182; // degrees longitude per mile
    const lowerLat = latitude - lat * distance;
    const lowerLon = longitude - lon * distance;
    const upperLat = latitude + lat * distance;
    const upperLon = longitude + lon * distance;
    const lower = encodeGeoHash(lowerLat, lowerLon);
    const upper = encodeGeoHash(upperLat, upperLon);
    return {
        lower,
        upper,
    };
};
// geohash.js
// Geohash library for Javascript
// (c) 2008 David Troy
// Distributed under the MIT License
// Downloaded 2021-01-21 from https://github.com/davetroy/geohash-js/blob/master/geohash.js
const BITS = [16, 8, 4, 2, 1];
const BASE32 = "0123456789bcdefghjkmnpqrstuvwxyz";
const NEIGHBORS = {
    right: { even: "bc01fg45238967deuvhjyznpkmstqrwx" },
    left: { even: "238967debc01fg45kmstqrwxuvhjyznp" },
    top: { even: "p0r21436x8zb9dcf5h7kjnmqesgutwvy" },
    bottom: { even: "14365h7k9dcfesgujnmqp0r2twvyx8zb" },
};
const BORDERS = {
    right: { even: "bcfguvyz" },
    left: { even: "0145hjnp" },
    top: { even: "prxz" },
    bottom: { even: "028b" },
};
NEIGHBORS.bottom.odd = NEIGHBORS.left.even;
NEIGHBORS.top.odd = NEIGHBORS.right.even;
NEIGHBORS.left.odd = NEIGHBORS.bottom.even;
NEIGHBORS.right.odd = NEIGHBORS.top.even;
BORDERS.bottom.odd = BORDERS.left.even;
BORDERS.top.odd = BORDERS.right.even;
BORDERS.left.odd = BORDERS.bottom.even;
BORDERS.right.odd = BORDERS.top.even;
export const refine_interval = (interval, cd, mask) => {
    if (cd & mask)
        interval[0] = (interval[0] + interval[1]) / 2;
    else
        interval[1] = (interval[0] + interval[1]) / 2;
};
export const calculateAdjacent = (srcHash, dir) => {
    srcHash = srcHash.toLowerCase();
    const lastChr = srcHash.charAt(srcHash.length - 1);
    const type = srcHash.length % 2 ? "odd" : "even";
    let base = srcHash.substring(0, srcHash.length - 1);
    if (BORDERS[dir][type].indexOf(lastChr) != -1)
        base = calculateAdjacent(base, dir);
    return base + BASE32[NEIGHBORS[dir][type].indexOf(lastChr)];
};
export const decodeGeoHash = (geohash) => {
    let is_even = 1;
    const lat = [];
    const lon = [];
    lat[0] = -90.0;
    lat[1] = 90.0;
    lon[0] = -180.0;
    lon[1] = 180.0;
    let lat_err = 90.0;
    let lon_err = 180.0;
    for (let i = 0; i < geohash.length; i++) {
        const c = geohash[i];
        const cd = BASE32.indexOf(c);
        for (let j = 0; j < 5; j++) {
            const mask = BITS[j];
            if (is_even) {
                lon_err /= 2;
                refine_interval(lon, cd, mask);
            }
            else {
                lat_err /= 2;
                refine_interval(lat, cd, mask);
            }
            is_even = !is_even;
        }
    }
    lat[2] = (lat[0] + lat[1]) / 2;
    lon[2] = (lon[0] + lon[1]) / 2;
    return { latitude: lat, longitude: lon };
};
export const encodeGeoHash = (latitude, longitude) => {
    let is_even = 1;
    const lat = [];
    const lon = [];
    let bit = 0;
    let ch = 0;
    const precision = 12;
    let geohash = "";
    lat[0] = -90.0;
    lat[1] = 90.0;
    lon[0] = -180.0;
    lon[1] = 180.0;
    while (geohash.length < precision) {
        if (is_even) {
            const mid = (lon[0] + lon[1]) / 2;
            if (longitude > mid) {
                ch |= BITS[bit];
                lon[0] = mid;
            }
            else
                lon[1] = mid;
        }
        else {
            const mid = (lat[0] + lat[1]) / 2;
            if (latitude > mid) {
                ch |= BITS[bit];
                lat[0] = mid;
            }
            else
                lat[1] = mid;
        }
        is_even = !is_even;
        if (bit < 4)
            bit++;
        else {
            geohash += BASE32[ch];
            bit = 0;
            ch = 0;
        }
    }
    return geohash;
};
//# sourceMappingURL=locations.js.map